import React, { useState } from 'react';
import apiService from '../../services/apiService';
import Modal from '../ui/Modal';
import { useI18n } from '../../utils/i18n';

// #530 (Scheibe 2 aus #310): Ticket abschliessen -- Pflichtzeiten (oder
// "Ohne Zeiten"), Planung bzw. Entplanen, und danach die Rueckfrage:
// letzter Block eines Vorgangs ("Vorgang vollstaendig") oder Ticket ohne
// Vorgang ("Brauchst du noch einen Folgetermin?") samt Folgeblock. Code
// unveraendert aus TicketDetail.js uebernommen, Muster aus #312/#471/#528.
//
// Den Hook ruft TicketDetail auf, die Dialoge rendern nur. Die Vorbelegung
// passiert in handleOpenCompleteModal, also bevor der Dialog eingehaengt wird.

const PROJECT_PROMPT_MODE = {
  EXISTING: 'existing',
  CONVERT: 'convert'
};

const INITIAL_PROJECT_COMPLETE_INFO = {
  projectId: null,
  projectName: null,
  subprojects: [],
  mode: PROJECT_PROMPT_MODE.EXISTING,
  sourceTicketId: null
};

const parseTicketBlockSuffix = (title) => {
  try {
    const raw = String(title || '');
    const match = raw.match(/\s*\((\d+)\)\s*$/);
    if (match) {
      const num = parseInt(match[1], 10);
      const baseTitle = raw.replace(/\s*\(\d+\)\s*$/, '').trim();
      return { baseTitle, existingBlock: Number.isNaN(num) ? null : num };
    }
    return { baseTitle: raw.trim(), existingBlock: null };
  } catch (_) {
    return { baseTitle: String(title || '').trim(), existingBlock: null };
  }
};

const normalizeBaseTitle = (baseTitle) => {
  try {
    return String(baseTitle || '').replace(/\s*-\s*$/, '').trim();
  } catch (_) {
    return String(baseTitle || '').trim();
  }
};

export function useAbschluss({ ticket, currentTicket, setCurrentTicket, currentUser, onClose, setError, setHasChanges }) {
  const { t } = useI18n();
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  // Vorgang vollständig Modal (sofortig, überall)
  const [showProjectCompletePrompt, setShowProjectCompletePrompt] = useState(false);
  const [projectCompleteInfo, setProjectCompleteInfo] = useState(() => ({ ...INITIAL_PROJECT_COMPLETE_INFO }));
  const [completeComment, setCompleteComment] = useState('');
  const [completing, setCompleting] = useState(false);
  const [pendingCloseReason, setPendingCloseReason] = useState(null);
  // Abschluss-Planung (Pflichtfelder)
  const [completeDate, setCompleteDate] = useState('');
  const [completeStart, setCompleteStart] = useState('');
  const [completeEnd, setCompleteEnd] = useState('');
  const [completePrefilled, setCompletePrefilled] = useState(false);
  const [completeValidationError, setCompleteValidationError] = useState(null);
  const [completeWithoutTimes, setCompleteWithoutTimes] = useState(false);
  const isConversionPrompt = projectCompleteInfo.mode === PROJECT_PROMPT_MODE.CONVERT;

  const handleOpenCompleteModal = () => {
    setError(null);
    setCompleteValidationError(null);
    setCompleteComment('');
    try {
      const hasPlanning = !!(currentTicket?.plannedStartDateTime && currentTicket?.plannedEndDateTime);
      if (hasPlanning) {
        const s = new Date(currentTicket.plannedStartDateTime);
        const e = new Date(currentTicket.plannedEndDateTime);
        const toDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        const toTime = (d) => `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        setCompleteDate(toDate(s));
        setCompleteStart(toTime(s));
        setCompleteEnd(toTime(e));
        setCompletePrefilled(true);
      } else {
        setCompleteDate('');
        setCompleteStart('');
        setCompleteEnd('');
        setCompletePrefilled(false);
      }
    } catch (_) {
      setCompleteDate('');
      setCompleteStart('');
      setCompleteEnd('');
      setCompletePrefilled(false);
    }
    setCompleteWithoutTimes(false);
    setShowCompleteModal(true);
  };

  const handleCompleteTicket = async (e) => {
    e.preventDefault();
    try {
      setCompleting(true);
      setCompleteValidationError(null);

      const currentUserEmail = currentUser?.mail || currentUser?.userPrincipalName || '';
      const assignedToValue = currentUserEmail || currentTicket.assignedTo || '';
      const resolvedTicket = currentTicket || ticket;

      if (!completeWithoutTimes) {
        // Validierung der Pflichtfelder
        if (!completeDate || !completeStart || !completeEnd) {
          setCompleteValidationError(t('ticketDetail.completeMissingFields', 'Bitte Datum, Startzeit und Endzeit angeben.'));
          return;
        }

        // Kombiniere Datum und Zeiten zu ISO
        const startLocal = new Date(`${completeDate}T${completeStart}`);
        const endLocal = new Date(`${completeDate}T${completeEnd}`);
        if (!(startLocal instanceof Date) || Number.isNaN(startLocal.getTime()) || !(endLocal instanceof Date) || Number.isNaN(endLocal.getTime())) {
          setCompleteValidationError(t('ticketDetail.completeInvalidFields', 'Ungültiges Datum oder Uhrzeit.'));
          return;
        }
        if (endLocal <= startLocal) {
          setCompleteValidationError(t('ticketDetail.completeEndBeforeStart', 'Die Endzeit muss nach der Startzeit liegen.'));
          return;
        }

        const startISO = startLocal.toISOString();
        const endISO = endLocal.toISOString();

        // Optionaler Kommentar (vor Planung)
        if (completeComment.trim()) {
          await apiService.addCommentToTicket(ticket.id, completeComment.trim());
        }

        // Planung aktualisieren oder erstellen (inkl. Graph via Server-API)
        try {
          const hasExistingPlanning = !!(currentTicket?.isPlanned || currentTicket?.plannedStartDateTime || currentTicket?.plannedEndDateTime || currentTicket?.calendarEventId);
          if (hasExistingPlanning) {
            const planningUpdateData = {
              startDateTime: startISO,
              endDateTime: endISO,
              isPlanned: true
            };
            if (currentUserEmail) planningUpdateData.assignedTo = currentUserEmail;
            if (currentTicket?.planningId) planningUpdateData.planningId = currentTicket.planningId;
            await apiService.updateTicketPlanning(ticket.id, planningUpdateData);
          } else {
            const planningData = {
              startDateTime: startISO,
              endDateTime: endISO
            };
            if (currentUserEmail) planningData.assignedTo = currentUserEmail;
            await apiService.planTicket(ticket.id, planningData);
          }
        } catch (planErr) {
          console.error('Error creating/updating planning on complete:', planErr);
          setError(t('ticketDetail.completePlanningError', 'Fehler beim Aktualisieren/Erstellen des Kalendereintrags.'));
          return;
        }
      } else {
        // Ohne Zeiten: optionalen Kommentar hinzufügen und bestehende Planung/Kalendereinträge entfernen
        if (completeComment.trim()) {
          await apiService.addCommentToTicket(ticket.id, completeComment.trim());
        }

        try {
          await apiService.unplanTicket(ticket.id);
        } catch (unplanErr) {
          console.error('Error unplanning ticket on complete without times:', unplanErr);
          setError(t('ticketDetail.completeUnplanError', 'Fehler beim Entfernen des Kalendereintrags.'));
          return;
        }
      }

      // Vorab-Check: Wird dies das letzte aktive Ticket im Vorgang sein?
      let closingInfo = null;
      try {
        closingInfo = await apiService.getTicketClosingInfo(ticket.id);
      } catch (_) {}

      // Set status to 'abgeschlossen' (server will archive ticket)
      await apiService.updateTicket(ticket.id, {
        status: 'abgeschlossen',
        assignedTo: assignedToValue
      });

      setHasChanges('completed');
      setShowCompleteModal(false);

      // Sofortiges Prompt: wenn letzter Block im Vorgang
      if (closingInfo && closingInfo.success === true && closingInfo.willBeLast === true && closingInfo.projectId) {
        setProjectCompleteInfo({
          projectId: closingInfo.projectId,
          projectName: closingInfo.projectName || null,
          subprojects: closingInfo.subprojects || [],
          mode: PROJECT_PROMPT_MODE.EXISTING,
          sourceTicketId: resolvedTicket?.id || ticket.id || null
        });
        setPendingCloseReason('completed');
        setShowProjectCompletePrompt(true);
      } else if (!(resolvedTicket?.projectId || resolvedTicket?.subprojectId)) {
        const { baseTitle } = parseTicketBlockSuffix(resolvedTicket?.title || '');
        const normalizedBase = normalizeBaseTitle(baseTitle);
        const rawTitle = String(resolvedTicket?.title || '').trim();
        const fallbackProjectName = normalizedBase || baseTitle || rawTitle || t('projects.completePrompt.defaultProjectName', 'Neuer Vorgang');
        setProjectCompleteInfo({
          projectId: null,
          projectName: fallbackProjectName,
          subprojects: [],
          mode: PROJECT_PROMPT_MODE.CONVERT,
          sourceTicketId: resolvedTicket?.id || ticket.id || null
        });
        setPendingCloseReason('completed');
        setShowProjectCompletePrompt(true);
      } else {
        onClose('completed');
      }
    } catch (err) {
      console.error('Error completing ticket:', err);
      setError('Fehler beim Abschließen des Tickets');
    } finally {
      setCompleting(false);
    }
  };

  const handleProjectPromptKeepOpen = async () => {
    try {
      const srcTicket = currentTicket || ticket;
      if (!srcTicket) {
        return;
      }

      const rawTitle = String(srcTicket.title || '').trim();
      const { baseTitle, existingBlock } = parseTicketBlockSuffix(rawTitle);
      const normalizedBaseCandidate = normalizeBaseTitle(baseTitle);
      const effectiveBaseTitle = normalizedBaseCandidate || baseTitle || rawTitle || t('projects.completePrompt.defaultBlockBase', 'Ticket');
      const assignedTo = currentUser?.mail || currentUser?.userPrincipalName || srcTicket.assignedTo || '';

      if (projectCompleteInfo.mode === PROJECT_PROMPT_MODE.CONVERT) {
        let projectId = srcTicket.projectId || null;
        if (!projectId) {
          try {
            const projectRes = await apiService.createProject({
              name: projectCompleteInfo.projectName || effectiveBaseTitle,
              description: '',
              responsibleEmail: null,
              status: 'wartend',
              turnusCount: null,
              turnusEvery: null,
              turnusUnit: null,
              subprojects: [],
              // #230 (L4): Kunde des Quelltickets auf den neuen Vorgang übernehmen,
              // sonst erben alle späteren Zeitblöcke nichts.
              customerId: srcTicket.customerId || null
            });
            projectId = projectRes?.project?.id || projectRes?.id || null;
          } catch (createErr) {
            console.error('Error creating project during conversion prompt:', createErr);
            return;
          }
        }

        if (!projectId) {
          console.error('Conversion prompt: missing project id after creation.');
          return;
        }

        const updatePayload = {
          projectId,
          subprojectId: null,
          status: 'abgeschlossen',
          assignedTo
        };
        if (existingBlock == null) {
          updatePayload.title = `${effectiveBaseTitle} - (1)`;
        }

        try {
          await apiService.updateTicket(srcTicket.id, updatePayload);
          setCurrentTicket(prev => (prev && prev.id === srcTicket.id ? { ...prev, status: 'abgeschlossen', projectId } : prev));
        } catch (updateErr) {
          console.error('Error updating ticket during conversion prompt:', updateErr);
          return;
        }

        const nextBlockNum = existingBlock ? existingBlock + 1 : 2;
        const newTitle = `${effectiveBaseTitle} - (${nextBlockNum})`;

        try {
          await apiService.createTicket({
            title: newTitle,
            description: srcTicket.description || '',
            priority: srcTicket.priority || 'normal',
            assignedTo,
            category: srcTicket.category || 'Projekt',
            tags: srcTicket.tags || [],
            calenderMarker: srcTicket.calenderMarker || null,
            projectId,
            subprojectId: null,
            // #230 (L4): Folgeticket erbt Kunde + Ansprechpartner (Server validiert den Kontakt)
            customerId: srcTicket.customerId || null,
            contactId: srcTicket.contactId || null
          });
          try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'followup_created_from_conversion_prompt' } })); } catch (_) {}
        } catch (createTicketErr) {
          console.error('Error creating follow-up ticket during conversion prompt:', createTicketErr);
        }
      } else {
        if (existingBlock == null) {
          try {
            // #631: Das Original ist hier schon archiviert. Ohne Status oeffnet
            // der Server es beim Umbenennen wieder.
            await apiService.updateTicket(srcTicket.id, {
              title: `${effectiveBaseTitle} - (1)`,
              projectId: srcTicket.projectId || projectCompleteInfo.projectId || null,
              subprojectId: srcTicket.subprojectId || null,
              status: 'abgeschlossen'
            });
          } catch (e) {
            console.error('Error normalizing original ticket title:', e);
          }
        }

        const nextBlockNum = existingBlock ? existingBlock + 1 : 2;
        const newTitle = `${effectiveBaseTitle} - (${nextBlockNum})`;
        const projectId = srcTicket.projectId || projectCompleteInfo.projectId || null;
        const subprojectId = srcTicket.subprojectId || null;

        try {
          await apiService.createTicket({
            title: newTitle,
            description: srcTicket.description || '',
            priority: srcTicket.priority || 'normal',
            assignedTo,
            category: srcTicket.category || 'Projekt',
            tags: srcTicket.tags || [],
            calenderMarker: srcTicket.calenderMarker || null,
            projectId,
            subprojectId
          });
          try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'followup_created_from_project_prompt' } })); } catch (_) {}
        } catch (e) {
          console.error('Error creating follow-up ticket from project prompt:', e);
        }
      }
    } catch (err) {
      console.error('Project complete prompt keep open error:', err);
    } finally {
      setShowProjectCompletePrompt(false);
      setProjectCompleteInfo(() => ({ ...INITIAL_PROJECT_COMPLETE_INFO }));
      const reason = pendingCloseReason || 'completed';
      setPendingCloseReason(null);
      try { onClose && onClose(reason); } catch (_) {}
    }
  };

  const handleProjectPromptConfirm = async () => {
    const isConversion = projectCompleteInfo.mode === PROJECT_PROMPT_MODE.CONVERT;
    try {
      if (!isConversion && projectCompleteInfo.projectId) {
        await apiService.archiveProjectIfEmpty(projectCompleteInfo.projectId);
        try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'project_archived_from_prompt' } })); } catch (_) {}
      } else {
        try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'ticket_completed_from_prompt' } })); } catch (_) {}
      }
    } catch (e) {
      if (isConversion) {
        console.error('Ticket completion confirmation error (conversion prompt):', e);
      } else {
        console.error('Error archiving project if empty (from TicketDetail):', e);
      }
    } finally {
      setShowProjectCompletePrompt(false);
      setProjectCompleteInfo(() => ({ ...INITIAL_PROJECT_COMPLETE_INFO }));
      const reason = pendingCloseReason || 'completed';
      setPendingCloseReason(null);
      try { onClose && onClose(reason); } catch (_) {}
    }
  };

  return {
    showCompleteModal, setShowCompleteModal, showProjectCompletePrompt, setShowProjectCompletePrompt, projectCompleteInfo, setProjectCompleteInfo,
    completeComment, setCompleteComment, completing, setPendingCloseReason, completeDate, setCompleteDate,
    completeStart, setCompleteStart, completeEnd, setCompleteEnd, completePrefilled, completeValidationError,
    completeWithoutTimes, setCompleteWithoutTimes, isConversionPrompt, handleOpenCompleteModal, handleCompleteTicket, handleProjectPromptKeepOpen,
    handleProjectPromptConfirm
  };
}

export default function AbschlussModal({ abschluss, currentTicket, getCurrentUserEmail }) {
  const { t } = useI18n();
  const {
    showCompleteModal, setShowCompleteModal, showProjectCompletePrompt, setShowProjectCompletePrompt, projectCompleteInfo, setProjectCompleteInfo,
    completeComment, setCompleteComment, completing, setPendingCloseReason, completeDate, setCompleteDate,
    completeStart, setCompleteStart, completeEnd, setCompleteEnd, completePrefilled, completeValidationError,
    completeWithoutTimes, setCompleteWithoutTimes, isConversionPrompt, handleCompleteTicket, handleProjectPromptKeepOpen, handleProjectPromptConfirm
  } = abschluss;
  return (
    <>
        {/* COMPLETE TICKET MODAL */}
        {showCompleteModal && (
        <Modal
          open
          onClose={() => setShowCompleteModal(false)}
          title={t('ticketDetail.completeTitle', 'Ticket abschließen')}
          className="add-comment-modal"
          maxWidth="600px"
        >

              <form onSubmit={handleCompleteTicket} className="comment-form">
                <div className="form-body">
                  {/* #39: Hinweis beim Abschließen eines fremd zugewiesenen Tickets */}
                  {(() => {
                    const me = (getCurrentUserEmail() || '').toLowerCase();
                    const assigned = (currentTicket?.assignedTo || '').toLowerCase();
                    if (!assigned || assigned === me) return null;
                    return (
                      <div className="field-hint" style={{ background: 'var(--color-warning-bg)', border: '1px solid var(--color-warning-border-soft)', color: 'var(--color-warning-text-darker)', borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>
                        {t('ticketDetail.completeForeignHint', 'Dieses Ticket ist {{assignee}} zugewiesen — es wird trotzdem in deinem Namen abgeschlossen.', { assignee: currentTicket.assignedTo })}
                      </div>
                    );
                  })()}
                  {/* Pflichtfelder: Datum, Start, Ende */}
                  {completeValidationError && (
                    <div className="error-message" style={{ marginBottom: 8 }}>
                      {completeValidationError}
                    </div>
                  )}
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="complete-date">{t('ticketDetail.completeDate', 'Datum')}</label>
                      <input
                        id="complete-date"
                        type="date"
                        value={completeDate}
                        onChange={(e) => setCompleteDate(e.target.value)}
                        className="form-input"
                        disabled={completing || completeWithoutTimes}
                        placeholder="YYYY-MM-DD"
                        style={{ backgroundColor: completeWithoutTimes ? 'var(--color-border-light)' : ((!completePrefilled && !completeDate) ? 'var(--color-field-empty)' : undefined) }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="complete-start">{t('ticketDetail.completeStart', 'Startzeit')}</label>
                      <input
                        id="complete-start"
                        type="time"
                        value={completeStart}
                        onChange={(e) => setCompleteStart(e.target.value)}
                        className="form-input"
                        disabled={completing || completeWithoutTimes}
                        placeholder="08:00"
                        style={{ backgroundColor: completeWithoutTimes ? 'var(--color-border-light)' : ((!completePrefilled && !completeStart) ? 'var(--color-field-empty)' : undefined) }}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="complete-end">{t('ticketDetail.completeEnd', 'Endzeit')}</label>
                      <input
                        id="complete-end"
                        type="time"
                        value={completeEnd}
                        onChange={(e) => setCompleteEnd(e.target.value)}
                        className="form-input"
                        disabled={completing || completeWithoutTimes}
                        placeholder="09:00"
                        style={{ backgroundColor: completeWithoutTimes ? 'var(--color-border-light)' : ((!completePrefilled && !completeEnd) ? 'var(--color-field-empty)' : undefined) }}
                      />
                    </div>
                  </div>
                  <div className="field-hint" style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 4, marginBottom: 8 }}>
                    {t('ticketDetail.completeHint', 'Bitte geben Sie Datum, Start- und Endzeit an. Bei bereits geplanter Zeit sind die Felder vorbelegt.')}
                  </div>
                <div className="form-row" style={{ display: 'flex', justifyContent: 'flex-start', alignSelf: 'flex-start', width: '100%', textAlign: 'left' }}>
                  <label htmlFor="complete-without-times" style={{ display: 'inline-flex', alignItems: 'center', marginTop: 4, cursor: 'pointer', alignSelf: 'flex-start', marginLeft: 0 }}>
                    <input
                      id="complete-without-times"
                      type="checkbox"
                      checked={completeWithoutTimes}
                      onChange={(e) => setCompleteWithoutTimes(e.target.checked)}
                      disabled={completing}
                      style={{ marginRight: 8 }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>{t('ticketDetail.completeWithoutTimes', 'Ohne Zeiten')}</span>
                  </label>
                </div>
                  <textarea
                    value={completeComment}
                    onChange={(e) => setCompleteComment(e.target.value)}
                    placeholder={t('ticketDetail.optionalComment', 'Optionalen Kommentar eingeben...')}
                    rows="6"
                    disabled={completing}
                    className="form-textarea"
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => setShowCompleteModal(false)}
                    className="btn btn-secondary"
                    disabled={completing}
                  >
                    {t('ticketDetail.cancel', 'Abbrechen')}
                  </button>
                  <button
                    type="submit"
                  disabled={completing || (!completeWithoutTimes && (!completeDate || !completeStart || !completeEnd))}
                    className="btn btn-primary"
                  >
                    {completing ? t('ticketDetail.adding', 'Hinzufügen...') : t('ticketDetail.complete', 'Abschließen')}
                  </button>
                </div>
              </form>
        </Modal>
        )}

      {/* VORGANG VOLLSTÄNDIG PROMPT (global, sofort) */}
      {showProjectCompletePrompt && (
        <Modal
          open
          onClose={() => { setShowProjectCompletePrompt(false); setProjectCompleteInfo(() => ({ ...INITIAL_PROJECT_COMPLETE_INFO })); setPendingCloseReason(null); }}
          title={isConversionPrompt ? t('projects.completePrompt.convertTitle', 'Brauchst du noch einen Folgetermin?') : t('projects.completePrompt.title', 'Vorgang vollständig')}
          maxWidth="520px"
        >
            <div className="form-body">
              <div style={{ padding: 8 }}>
                {isConversionPrompt
                  ? t('projects.completePrompt.convertMessage', 'Brauchst du noch einen Folgetermin? Oder ist wirklich alles zu dem Thema abgeschlossen?')
                  : t('projects.completePrompt.message', 'Der letzte Zeitblock des Vorgangs wurde abgeschlossen. Soll der Vorgang (und seine Untervorgänge) auch als abgeschlossen markiert und archiviert werden?')}
                <div style={{ marginTop: 8, color: 'var(--color-text-dark-soft)' }}>
                  {projectCompleteInfo.projectName ? (
                    <div>
                      <strong>{t('projects.completePrompt.project', 'Vorgang')}:</strong> {projectCompleteInfo.projectName}
                    </div>
                  ) : null}
                  {!isConversionPrompt && Array.isArray(projectCompleteInfo.subprojects) && projectCompleteInfo.subprojects.length > 0 ? (
                    <div style={{ marginTop: 6 }}>
                      <strong>{t('projects.completePrompt.subprojects', 'Teilvorgänge')}:</strong>
                      <ul style={{ marginTop: 4, marginBottom: 0, paddingLeft: 18 }}>
                        {projectCompleteInfo.subprojects.map(sp => (
                          <li key={sp.id}>
                            {sp.name} {sp.subprojectNumber ? `(${sp.subprojectNumber})` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleProjectPromptKeepOpen}
              >
                {t('projects.completePrompt.keepOpen', 'Nein, ich brauche noch Zeit')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleProjectPromptConfirm}
              >
                {isConversionPrompt
                  ? t('projects.completePrompt.confirmConvert', 'Bin fertig, abschließen')
                  : t('projects.completePrompt.confirm', 'Ja, Vorgang archivieren')}
              </button>
            </div>
        </Modal>
      )}
    </>
  );
}
